import React, { useState, useCallback, useMemo } from 'react';
import { bool, node } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';
import { useParams } from 'react-router-dom';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { Form, PrimaryButton, FieldTextInput, FieldSelect } from '../../../components';
import { RECAPTCHA_SITE_KEY, isRecaptchaEnabled } from '../../../util/recaptcha';

import css from './SignupForm.module.css';
import { hearAboutOptions } from '../utils';

// PERF: Memoize array mutators to prevent recreation
const formMutators = { ...arrayMutators };

/** Form UI shared by both captcha and no-captcha flows */
const SignupFormFields = props => {
  const { onSubmit, intl, captchaError, ...rest } = props;

  // PERF: Memoize validation messages to prevent recalculation on each render
  const validationMessages = useMemo(
    () => ({
      emailRequired: intl.formatMessage({ id: 'SignupForm.emailRequired' }),
      emailInvalid: intl.formatMessage({ id: 'SignupForm.emailInvalid' }),
      emailLabel: intl.formatMessage({ id: 'SignupForm.emailLabel' }),
      emailPlaceholder: intl.formatMessage({ id: 'SignupForm.emailPlaceholder' }),
      firstNameLabel: intl.formatMessage({ id: 'SignupForm.firstNameLabel' }),
      firstNamePlaceholder: intl.formatMessage({ id: 'SignupForm.firstNamePlaceholder' }),
      firstNameRequired: intl.formatMessage({ id: 'SignupForm.firstNameRequired' }),
      lastNameLabel: intl.formatMessage({ id: 'SignupForm.lastNameLabel' }),
      lastNamePlaceholder: intl.formatMessage({ id: 'SignupForm.lastNamePlaceholder' }),
      lastNameRequired: intl.formatMessage({ id: 'SignupForm.lastNameRequired' }),
      passwordLabel: intl.formatMessage({ id: 'SignupForm.passwordLabel' }),
      passwordPlaceholder: intl.formatMessage({ id: 'SignupForm.passwordPlaceholder' }),
      passwordRequired: intl.formatMessage({ id: 'SignupForm.passwordRequired' }),
      passwordTooShort: intl.formatMessage(
        { id: 'SignupForm.passwordTooShort' },
        { minLength: validators.PASSWORD_MIN_LENGTH }
      ),
      passwordTooLong: intl.formatMessage(
        { id: 'SignupForm.passwordTooLong' },
        { maxLength: validators.PASSWORD_MAX_LENGTH }
      ),
      nameTooShort: intl.formatMessage({
        id: 'SignupForm.nameTooShort',
        defaultMessage: 'Please enter at least 2 characters.',
      }),
      passwordFormat: intl.formatMessage({
        id: 'SignupForm.passwordFormat',
        defaultMessage:
          'Password needs an uppercase letter, a lowercase letter, a number and a special character.',
      }),
    }),
    [intl]
  );

  // PERF: Memoize validators to prevent recreation on each render
  const emailValidators = useMemo(() => {
    const emailRequired = validators.required(validationMessages.emailRequired);
    const emailValid = validators.emailFormatValid(validationMessages.emailInvalid);
    return validators.composeValidators(emailRequired, emailValid);
  }, [validationMessages.emailRequired, validationMessages.emailInvalid]);

  const firstNameValidator = useMemo(() => {
    return validators.composeValidators(
      validators.required(validationMessages.firstNameRequired),
      validators.nameMinLength(validationMessages.nameTooShort)
    );
  }, [validationMessages.firstNameRequired, validationMessages.nameTooShort]);

  const lastNameValidator = useMemo(() => {
    return validators.composeValidators(
      validators.required(validationMessages.lastNameRequired),
      validators.nameMinLength(validationMessages.nameTooShort)
    );
  }, [validationMessages.lastNameRequired, validationMessages.nameTooShort]);

  const passwordValidators = useMemo(() => {
    const passwordRequired = validators.requiredStringNoTrim(validationMessages.passwordRequired);
    const passwordMinLength = validators.minLength(
      validationMessages.passwordTooShort,
      validators.PASSWORD_MIN_LENGTH
    );
    const passwordMaxLength = validators.maxLength(
      validationMessages.passwordTooLong,
      validators.PASSWORD_MAX_LENGTH
    );
    const passwordFormat = validators.passwordFormatValid(validationMessages.passwordFormat);
    return validators.composeValidators(
      passwordRequired,
      passwordMinLength,
      passwordMaxLength,
      passwordFormat
    );
  }, [
    validationMessages.passwordRequired,
    validationMessages.passwordTooShort,
    validationMessages.passwordTooLong,
    validationMessages.passwordFormat,
  ]);

  return (
    <FinalForm
      {...rest}
      onSubmit={onSubmit}
      mutators={formMutators}
      render={fieldRenderProps => {
        const {
          rootClassName,
          className,
          formId,
          handleSubmit,
          inProgress,
          invalid,
          termsAndConditions,
        } = fieldRenderProps;

        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = inProgress;
        const submitDisabled = invalid || submitInProgress;

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            <div>
              <FieldTextInput
                type="email"
                id={formId ? `${formId}.email` : 'email'}
                name="email"
                autoComplete="email"
                label={validationMessages.emailLabel}
                placeholder={validationMessages.emailPlaceholder}
                validate={emailValidators}
              />
              <div className={css.name}>
                <FieldTextInput
                  className={css.firstNameRoot}
                  type="text"
                  id={formId ? `${formId}.fname` : 'fname'}
                  name="fname"
                  autoComplete="given-name"
                  label={validationMessages.firstNameLabel}
                  placeholder={validationMessages.firstNamePlaceholder}
                  validate={firstNameValidator}
                />
                <FieldTextInput
                  className={css.lastNameRoot}
                  type="text"
                  id={formId ? `${formId}.lname` : 'lname'}
                  name="lname"
                  autoComplete="family-name"
                  label={validationMessages.lastNameLabel}
                  placeholder={validationMessages.lastNamePlaceholder}
                  validate={lastNameValidator}
                />
              </div>
              <FieldTextInput
                className={css.password}
                type="password"
                id={formId ? `${formId}.password` : 'password'}
                name="password"
                autoComplete="new-password"
                label={validationMessages.passwordLabel}
                placeholder={validationMessages.passwordPlaceholder}
                validate={passwordValidators}
              />

              <p className={css.passwordRequirements}>
                <FormattedMessage id="SignupForm.passwordRequirements" />
              </p>

              {/* <FieldSelect
                id="heardAbout"
                name="heardAbout"
                className={css.hearAboutField}
                label={intl.formatMessage({ id: 'SignupForm.hearabout' })}
                validate={validators.required(<FormattedMessage id="SignupForm.field" />)}
              >
                <option disabled value="">
                  {intl.formatMessage({ id: 'SignupForm.select' })}
                </option>
                {hearAboutOptions.map(o => (
                  <option key={o.key} value={o.key}>
                    {intl.formatMessage({ id: o.messageID })}
                  </option>
                ))}
              </FieldSelect> */}
            </div>

            {captchaError ? <p className={css.captchaError}>{captchaError}</p> : null}

            <div className={css.bottomWrapper}>
              {termsAndConditions}
              <PrimaryButton
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled}
                data-elbaction="click:click registbutton"
              >
                <FormattedMessage id="SignupForm.signUp" />
              </PrimaryButton>
            </div>
          </Form>
        );
      }}
    />
  );
};

/** Wrapper that adds reCAPTCHA verification (only rendered when provider is mounted) */
const SignupFormWithCaptcha = props => {
  const { lang: currentLanguage } = useParams();
  const { onSubmit, intl, ...rest } = props;
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [captchaError, setCaptchaError] = useState(null);

  const handleFormSubmit = useCallback(
    async values => {
      // Fetch a reCAPTCHA token and hand it to the signup endpoint, which
      // verifies it SERVER-SIDE and rejects bots before creating an account.
      // Previously this was verified here in the browser (advisory only) and a
      // bot hitting the API directly skipped it entirely.
      let recaptchaToken;
      if (executeRecaptcha) {
        setCaptchaError(null);
        try {
          recaptchaToken = await executeRecaptcha('signup');
        } catch (error) {
          console.error('reCAPTCHA error:', error);
          setCaptchaError(intl.formatMessage({ id: 'SignupForm.captchaError' }));
          return;
        }
      }
      const enrichedValues = { ...values, language: currentLanguage, recaptchaToken };
      return onSubmit(enrichedValues);
    },
    [executeRecaptcha, onSubmit, currentLanguage, intl]
  );

  return (
    <SignupFormFields
      {...rest}
      intl={intl}
      onSubmit={handleFormSubmit}
      captchaError={captchaError}
    />
  );
};

const SignupFormComponent = props => {
  const { lang: currentLanguage } = useParams();
  const captchaRequired = !!RECAPTCHA_SITE_KEY && isRecaptchaEnabled();
  if (captchaRequired) {
    return (
      <GoogleReCaptchaProvider
        reCaptchaKey={RECAPTCHA_SITE_KEY}
        language={currentLanguage}
        scriptProps={{
          async: true,
          defer: true,
          appendTo: 'head',
          id: 'recaptcha-signup',
        }}
        useEnterprise={false}
      >
        <SignupFormWithCaptcha {...props} />
      </GoogleReCaptchaProvider>
    );
  }
  const onSubmitWithLanguage = values => props.onSubmit({ ...values, language: currentLanguage });
  return <SignupFormFields {...props} onSubmit={onSubmitWithLanguage} captchaError={null} />;
};

SignupFormComponent.defaultProps = { inProgress: false };

SignupFormComponent.propTypes = {
  inProgress: bool,
  termsAndConditions: node.isRequired,
  // from injectIntl
  intl: intlShape.isRequired,
  onSubmit: Function.isRequired,
};

const SignupForm = compose(injectIntl)(SignupFormComponent);
SignupForm.displayName = 'SignupForm';

export default SignupForm;
